import {
  GRAMMAR,
  LANGUAGE,
  NEW,
  PLAGIARISM,
  READ,
  SPELLCHECK,
} from '@src/components/Cards/constants'
import { Status } from '@src/components/Modals/PlagiarismCheckModal'
import { CheckState } from '@src/state/checks/reducer'
import { getPlagiarismMetadataId } from '@src/state/modals/selectors'
import { RootState } from '@src/state/types'
import {
  Check,
  CheckType,
  CommentState,
  PlagiarismGlimpse,
  PlagiarismReplacement,
} from '@src/types/checks'
import { getCurrentLocale } from '@src/utils/locale'
import { createSelector, Selector } from 'reselect'
import {
  selectAllComments,
  selectInteractableGrammarComments,
  selectInteractablePlagComments,
} from '../document/selectors'
import { CheckCategory } from '../document/types'
import { CardActionsState } from './actions'
import { AnalyzeOptions } from './types'

export const getCommentsByType = (type: string) =>
  createSelector([(state: RootState) => selectAllComments(state)], comments => {
    return comments.filter(
      comment =>
        comment.type === type && (comment.cardState === NEW || comment.cardState === READ)
    )
  })

export const getGrammarCommentsFromEditor = createSelector(
  [(state: RootState) => selectInteractableGrammarComments(state)],
  comments => comments.filter(comment => comment.type !== SPELLCHECK)
)

export const selectSpellcheckLanguageCommentCounts = createSelector(
  [
    getGrammarCommentsFromEditor,
    (state: RootState) => getCommentsByType(SPELLCHECK)(state),
  ],
  (language, spellcheck) => ({
    [SPELLCHECK]: spellcheck.length,
    [LANGUAGE]: language.length,
  })
)

export const selectSumSpellcheckLanguageComments = createSelector(
  [selectSpellcheckLanguageCommentCounts],
  grammarCounts => grammarCounts[LANGUAGE] + grammarCounts[SPELLCHECK]
)

export interface CategorizedCounts {
  [PLAGIARISM]: number
  [GRAMMAR]: number
}

export const plagiarismCheckStatus = (state: RootState): Status => {
  const { checksRunning, plagiarismAnalyzeFailed } = state.workspace
  const { checks } = state

  if (checksRunning) {
    return 'RUNNING'
  }

  if (
    plagiarismAnalyzeFailed ||
    (checks.plagiarism && checks.plagiarism.status === 'FAILED')
  ) {
    return 'FAILED'
  }
  return 'COMPLETED'
}

export const plagiarismCheckResultCount = (checks: CheckState): number => {
  if (checks.plagiarism && checks.plagiarism.finished && checks.plagiarism.count) {
    return checks.plagiarism.count
  }
  return 0
}

export const selectCheckTypeMetadataById = (
  idSelector: Selector<RootState, string | undefined>,
  category: CheckCategory
) =>
  createSelector([idSelector, getCommentsByType(category)], (id, metadata) => {
    if (id && metadata) {
      return metadata.find(checkMetadata => checkMetadata.id === id)
    }
    return undefined
  })

export const selectPlagiarismModalCitations = createSelector(
  [selectCheckTypeMetadataById(getPlagiarismMetadataId, PLAGIARISM)],
  check => {
    if (check) {
      return check.replacements.map(({ cite }: PlagiarismReplacement) => cite)
    }
    return undefined
  }
)

export const selectPlagiarismGlimpse = createSelector(
  state => selectInteractablePlagComments(state),
  getPlagiarismMetadataId,
  (plagComments, id): PlagiarismGlimpse | undefined => {
    const comment = plagComments.find(c => c.id === id)
    if (comment) {
      return {
        submissionId: comment.submissionId,
        glimpseId: comment.glimpseId,
      }
    }
    return undefined
  }
)

export const shouldCardUpdate = (oldState: CommentState, newState: CardActionsState) =>
  oldState === 'new' || (oldState === 'read' && newState !== 'read')

export const allChecksFailed = (checks: any) => {
  const checksWithoutPlagiarism = Object.keys(checks).reduce(
    (checkArr: Check[], check: string): Check[] => {
      if (check === 'plagiarism') {
        return checkArr
      }
      checkArr.push(checks[check])
      return checkArr
    },
    []
  )

  return checksWithoutPlagiarism.every((check: Check) => check.status === 'FAILED')
}

export const allChecksComplete = (
  checkState: CheckState,
  checksToExpect?: CheckType[]
): boolean => {
  if (!checksToExpect || !checksToExpect.length) {
    return false
  }

  let allChecksDone = true

  checksToExpect.forEach(check => {
    if (!checkState[check] || checkState[check].finished === false) {
      allChecksDone = false
    }
  })
  return allChecksDone
}

export const getIncompleteGrammarChecks = (state: RootState) => {
  const { checksToExpect } = state.workspace
  const incompleteChecks: CheckType[] = []

  if (checksToExpect) {
    checksToExpect.forEach(check => {
      if (
        state.checks[check] &&
        (state.checks[check].finished === false ||
          state.checks[check].status === 'FAILED')
      ) {
        incompleteChecks.push(check)
      }
    })
  }
  return incompleteChecks
}

export const selectAnalyzeOptions = (): AnalyzeOptions => {
  const locale = getCurrentLocale()
  return {
    locale,
  }
}



// WEBPACK FOOTER //
// ./src/state/checks/selectors.ts